import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, FolderOpen, Edit2, X, Save, ExternalLink, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import { getDocuments, createDocument, updateDocument, formatDate, getApiError } from '../api';

const QdDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  
  useEffect(() => {
    loadDocuments();
  }, []);
  
  const loadDocuments = async () => {
    try {
      const response = await getDocuments();
      setDocuments(response.data);
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setLoading(false);
    }
  };
  
  const handleTogglePublish = async (doc) => {
    try {
      await updateDocument(doc.id, { ...doc, published: !doc.published });
      toast.success(doc.published ? 'Dokumentas paslėptas' : 'Dokumentas paskelbtas');
      loadDocuments();
    } catch (error) {
      toast.error(getApiError(error));
    }
  };
  
  const filteredDocs = documents.filter(doc => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      doc.title?.toLowerCase().includes(searchLower) ||
      doc.category?.toLowerCase().includes(searchLower)
    );
  });
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dokumentai</h1>
          <p className="text-gray-500">{filteredDocs.length} dokumentų</p>
        </div>
        <button
          onClick={() => { setEditingDoc(null); setShowModal(true); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0F7D70] text-white rounded-lg hover:bg-[#0a5f55] transition-colors"
        >
          <Plus size={20} />
          Naujas dokumentas
        </button>
      </div>
      
      {/* Search */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Ieškoti dokumentų..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
          />
        </div>
      </div>
      
      {/* Documents List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F7D70]" />
        </div>
      ) : filteredDocs.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-100 text-center">
          <FolderOpen size={48} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">Nėra dokumentų</h3>
          <p className="text-gray-500">
            {search ? 'Pagal jūsų paiešką nieko nerasta' : 'Pridėkite pirmą dokumentą'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pavadinimas</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kategorija</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kalba</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Versija</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Būsena</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Atnaujinta</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Veiksmai</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredDocs.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">{doc.title}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-600">{doc.category || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                        {doc.language?.toUpperCase() || 'LT'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-600">{doc.version || '-'}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleTogglePublish(doc)}
                        className={`
                          inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors
                          ${doc.published 
                            ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}
                        `}
                      >
                        {doc.published ? <Eye size={14} /> : <EyeOff size={14} />}
                        {doc.published ? 'Paskelbtas' : 'Paslėptas'}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-500">{formatDate(doc.updated_at)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {doc.url && (
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <ExternalLink size={16} />
                          </a>
                        )}
                        <button
                          onClick={() => { setEditingDoc(doc); setShowModal(true); }}
                          className="p-2 text-gray-400 hover:text-[#0F7D70] hover:bg-[#0F7D70]/10 rounded-lg transition-colors"
                        >
                          <Edit2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Modal */}
      {showModal && (
        <DocumentModal
          document={editingDoc}
          onClose={() => setShowModal(false)}
          onSaved={() => {
            setShowModal(false);
            loadDocuments();
          }}
        />
      )}
    </div>
  );
};

// Document Modal
const DocumentModal = ({ document, onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: document?.title || '',
    category: document?.category || '',
    language: document?.language || 'lt',
    version: document?.version || '',
    url: document?.url || '',
    published: document?.published || false
  });
  const [saving, setSaving] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title.trim()) {
      toast.error('Įveskite dokumento pavadinimą');
      return;
    }
    
    if (!form.url.trim()) {
      toast.error('Įveskite dokumento URL');
      return;
    }
    
    setSaving(true);
    try {
      if (document) {
        await updateDocument(document.id, form);
        toast.success('Dokumentas atnaujintas');
      } else {
        await createDocument(form);
        toast.success('Dokumentas sukurtas');
      }
      onSaved();
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FolderOpen size={20} className="text-[#0F7D70]" />
            {document ? 'Redaguoti dokumentą' : 'Naujas dokumentas'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pavadinimas *
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kategorija
              </label>
              <input
                type="text"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder="Pvz.: Datasheet"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Versija
              </label>
              <input
                type="text"
                value={form.version}
                onChange={(e) => setForm({ ...form, version: e.target.value })}
                placeholder="Pvz.: 1.0"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kalba
            </label>
            <select
              value={form.language}
              onChange={(e) => setForm({ ...form, language: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
            >
              <option value="lt">Lietuvių</option>
              <option value="lv">Latviešu</option>
              <option value="et">Eesti</option>
              <option value="en">English</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL *
            </label>
            <input
              type="url"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="https://..."
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
              required
            />
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="published"
              checked={form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })}
              className="w-4 h-4 text-[#0F7D70] border-gray-300 rounded focus:ring-[#0F7D70]"
            />
            <label htmlFor="published" className="text-sm text-gray-700">
              Paskelbti (matomas visiems vartotojams)
            </label>
          </div>
          
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Atšaukti
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2.5 bg-[#0F7D70] text-white rounded-lg hover:bg-[#0a5f55] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? 'Saugoma...' : (
                <>
                  <Save size={18} />
                  {document ? 'Atnaujinti' : 'Sukurti'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QdDocuments;
